import React, { useMemo } from "react";
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Image, Platform, TextInput } from "react-native";
import { useTranslation } from 'react-i18next';
import GradientBackground from '../../../../uiComponents/GradientBackground';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import DownArrow from "../../../../../../../../assets/images/DownArrow.png";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

import ReactCountryFlag from 'react-country-flag';
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

interface ReviewFinalStepProps {
  onContinue: () => void;
  onEditStep: (key: string) => void;
}

const ReviewFinalStep: React.FC<ReviewFinalStepProps> = ({ onEditStep, onContinue }) => {
  const { t } = useTranslation();
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const currentSendAmount = useSessionStore((s: SessionState) => (s as any).currentSendAmount);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => s.selectedReceiveCurrency);
  const feeLookupParams = useSessionStore((s: SessionState) => (s as any).sendFeeLookupReviewEditStore);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const senderFirstName = useSessionStore((s: SessionState) => (s as any).senderFirstName);
  const senderMiddleName = useSessionStore((s: SessionState) => (s as any).senderMiddleName);
  const senderLastName = useSessionStore((s: SessionState) => (s as any).senderLastName);
  const senderLastName2 = useSessionStore((s: SessionState) => (s as any).senderLastName2);
  const senderAddress = useSessionStore((s: SessionState) => (s as any).senderAddress);
  const senderCity = useSessionStore((s: SessionState) => (s as any).senderCity);
  const senderZipCode = useSessionStore((s: SessionState) => (s as any).senderZipCode);
  const senderState = useSessionStore((s: SessionState) => (s as any).senderState);
  const senderDOB = useSessionStore((s: SessionState) => (s as any).senderDOB);
  const senderDOBString = useSessionStore((s: SessionState) => (s as any).senderDOBString);
  const phoneNumber = useSessionStore((s: SessionState) => (s as any).phoneNumber);
  const customerReceiveNumber = useSessionStore((s: SessionState) => (s as any).customerReceiveNumber);
  const senderPhotoIdNumber = useSessionStore((s: SessionState) => (s as any).senderPhotoIdNumber);
  // fetch additional information dynamically
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;
  const partnerName = useSessionStore((s: SessionState) => (s as any).partnerName);
  const partnerAddress = useSessionStore((s: SessionState) => (s as any).partnerAddress);

  console.log('partnerName();==>', partnerName, "partnerAddress=>", partnerAddress)

  const option: any = pendingSendOption || selectedSendOption || null;

  const alpha2 = useMemo(() => {
    const a3 = (selectedDestinationCountryCode || '').toString().toUpperCase();
    return countries.alpha3ToAlpha2(a3) || '';
  }, [selectedDestinationCountryCode]);

  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper].map((c) => String.fromCodePoint(base + c.charCodeAt(0))).join("");
  };
  const selectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).selectedDeliveryOptionStore);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);

  const getValuesFromLatestFeeLookupResponse = lastFeeLookupResponse?.FeeInfos.find((f: any) => {
    const sameDisplay = (f?.DeliveryOption || '') === (selectedDeliveryOptionStore?.DeliveryOption || '');
    return sameDisplay;
  });

  
  const summary = useMemo(() => {
    const sendCurrency: string = getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendCurrency || "USD";
    const amountFromState = Number(String(currentSendAmount || "0").replace(/,/g, ""));
    const amountFromOption = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.SendAmount ?? 0);
    const sendAmountNum: number = isFinite(amountFromOption) && amountFromOption > 0
      ? amountFromOption
      : (isFinite(amountFromState) ? amountFromState : 0);
    const receiveCurrency: string = selectedReceiveCurrency || (getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getReceiveCurrency(getValuesFromLatestFeeLookupResponse) : "");
    const receiveAmount: number = getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getReceiveAmount(getValuesFromLatestFeeLookupResponse) : 0;
    const exchangeRateNum: number = getValuesFromLatestFeeLookupResponse ? FeeLookupHelper.getExchangeRate(getValuesFromLatestFeeLookupResponse) : 0;
    const fees: number = Number(getValuesFromLatestFeeLookupResponse?.SendAmounts?.TotalSendFees || 0);
    const total: number = Number(getValuesFromLatestFeeLookupResponse?.TotalAmount);

    return {
      sendAmount: sendAmountNum,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
      exchangeRate: exchangeRateNum,
      fees,
      total,
    };
  }, [option, currentSendAmount, selectedReceiveCurrency]);

  const promoCodeValue = useMemo(() => {
    try {
      const arr: any[] = Array.isArray((feeLookupParams as any)?.promoCodes) ? (feeLookupParams as any).promoCodes : [];
      return String(arr[0] ?? "");
    } catch { return ""; }
  }, [feeLookupParams]);

  // Promo code is read-only on this screen

  const receiverName = useMemo(() => {
    return [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
      .filter(Boolean)
      .join(" ");
  }, [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]);

  const senderName = useMemo(() => {
    return [senderFirstName, senderMiddleName, senderLastName, senderLastName2]
      .filter(Boolean)
      .join(" ");
  }, [senderFirstName, senderMiddleName, senderLastName, senderLastName2]);

  const senderAddressFull = useMemo(() => {
    const parts = [
      senderAddress,
      [senderCity, (senderState || selectedDestinationStateAbbr)].filter(Boolean).join(", "),
      senderZipCode,
    ].filter(Boolean);
    return parts.join("\n");
  }, [senderAddress, senderCity, senderState, selectedDestinationStateAbbr, senderZipCode]);

  const idNumberToShow = customerReceiveNumber || senderPhotoIdNumber || "";

  const dobToShow = useMemo(() => {
    if (senderDOBString) return senderDOBString;
    if (senderDOB instanceof Date && !isNaN((senderDOB as any))) {
      const mm = String(senderDOB.getMonth() + 1).padStart(2, "0");
      const dd = String(senderDOB.getDate()).padStart(2, "0");
      const yyyy = senderDOB.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }
    return "";
  }, [senderDOBString, senderDOB]);

  const additionalInfo = useMemo(() => {
    return Object.values(additionalFieldValues).filter(Boolean).join(', ');
  }, [additionalFieldValues]);


  const data = {
    sendAmount: summary.sendAmount.toFixed(2),
    sendCurrency: summary.sendCurrency,
    receiveAmount: summary.receiveAmount.toFixed(2),
    receiveCurrency: summary.receiveCurrency || "—",
    receiverName: receiverName || "",
    destinationCountry: selectedDestinationCountryCode || "",
    destinationState: selectedDestinationStateAbbr || "",
    receiveMethod: option?.DeliveryOption || "",
    senderName: senderName || "",
    senderAddress: senderAddressFull || "",
    phoneNumber: phoneNumber || "",
    idNumber: idNumberToShow || "",
    dob: dobToShow || "",
    exchangeRate: summary.exchangeRate ? summary.exchangeRate.toFixed(4) : "0.0000",
    available: "Instantly",
    fees: summary.fees.toFixed(2),
    total: summary.total.toFixed(2),
    additionalInformation: additionalInfo || "",
  };
  return (
    <View style={styles.container}>
      <ScrollView style={{ flex: 1 }}>
        {/* Title */}
        <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>
        <Text style={styles.subtitle}>{t("REVIEW_SUBTITLE")}</Text>
        <View>
          <Text style={styles.inputLabel}>{t("CVS_STORE_LOCATION")}</Text>
          <Text style={[styles.inputLabel, {fontSize: 16, lineHeight: 20, color: Colors.titleTextColor}]}>{partnerAddress}</Text>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 16}}>
          <View>
            <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
            <View style={styles.amountInput}>
              <Text style={styles.amountText}>{data.sendAmount}</Text>
              <Text style={styles.currencyText}>{data.sendCurrency}</Text>
            </View>
            <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_THEY_RECEIVE")}</Text>
            <View style={styles.amountInput}>
              <Text style={styles.amountText}>{data.receiveAmount}</Text>
              <Text style={styles.currencyText}>{data.receiveCurrency}</Text>
            </View>
          </View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('sendingAmountEntry')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
          <View>
            <Text style={[styles.inputLabel, {marginTop: 8}]}>Receivers Name</Text>
            <Text style={[styles.pickUpText]}>{data.receiverName}</Text>
          </View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('receiverName')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("REVIEW_DESTINATION_COUNTRY")}</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
          {Platform.OS === 'web' ? (
            alpha2 ? (
              <ReactCountryFlag
                countryCode={alpha2}
                svg
                style={{ width: 20, height: 20, borderRadius: 50 }}
                title={alpha2}
              />
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )
          ) : (
            <Text style={{ fontSize: 16 }}>{countryCodeToEmoji(alpha2) || '🌍'}</Text>
          )}
            <Text style={styles.CountryText}>{data.destinationCountry}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('country')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      { (selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') &&
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Destination State</Text>
          <View style={[styles.amountInput, { marginTop: 2, alignItems: 'center'}]}>
            <Image
              source={EnglishFlag}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
            <Text style={styles.CountryText}>{data.destinationState}</Text>
          </View>
        </View>
        <View>
          <TouchableOpacity
            style={[styles.editButton, { backgroundColor: '#FFFFFF',}]}
            onPress={() => onEditStep?.('state')}>
            <Image
              source={DownArrow}
              style={{height: 20, width: 20, borderRadius: 50}}
              resizeMode='contain'           
            />
          </TouchableOpacity>
        </View>
      </View>
      }
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>{t("RECEIVE_METHOD_TITLE")}</Text>
          <Text style={[styles.pickUpText, { width: 250 }]}>{pendingSendOption?.DeliveryOptDisplayName}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('howTheyReceive')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Senders Name</Text>
          <Text style={[styles.pickUpText]}>{data.senderName}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('senderName')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Senders Address</Text>
          <Text style={[styles.pickUpText]}>{data.senderAddress}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('senderAddress')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>Phone Number</Text>
          <Text style={[styles.pickUpText]}>{data.phoneNumber}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('phone')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>ID Number</Text>
          <Text style={[styles.pickUpText]}>{data.idNumber}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('senderIdNumber')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
        <View>
          <Text style={[styles.inputLabel, {marginTop: 8}]}>DOB</Text>
          <Text style={[styles.pickUpText]}>{data.dob}</Text>
        </View>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => onEditStep?.('senderDOB')}>
          <Text style={styles.editText}>Edit</Text>
        </TouchableOpacity>
      </View>
      { Object.keys(additionalFieldValues).length !== 0 &&
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 32}}>
          <View>
            <Text style={[styles.inputLabel, {marginTop: 8}]}>Additional Information</Text>
            <Text style={[styles.pickUpText]}>{data.additionalInformation}</Text>
          </View>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => onEditStep?.('additionalInformation')}>
            <Text style={styles.editText}>Edit</Text>
          </TouchableOpacity>
        </View>
      }
      <View style={styles.promoContainer}>
        <TextInput
          style={styles.promoInput}
          placeholder="Promo code"
          value={promoCodeValue}
          editable={false}
        />
      </View>
        {/* <RowItem
          label="DOB"
          value={data.dob}
          editable
          onEditStep={() => onEditStep("dob")}
        /> */}

        {/* Terms */}
      <View style={{ marginTop: 16 }}>
        <Text style={styles.terms}>
          I agree to MoneyGram’s <Text style={styles.link}>Terms and Conditions</Text> and confirm I am the sole beneficial owner of the assets, and am acting solely on my own profile. By continuing with this transaction I consent to the collection, use, disclosure, and transfer (including cross-border transfer) of my personal information as described in our Privacy Policy.
        </Text>
        <Text style={[styles.terms, { marginTop: 16, marginBottom: 24}]}>
        <Text style={styles.bold}>Exchange rate</Text>{"\n"}
          In addition to transfer fees, a currency exchange rate, also determined by MoneyGram, may also apply. MoneyGram may also make money from currency exchange. When selecting a money transfer operator, carefully compare both transfer fees and currency exchange rates. Transfer fees may vary depending on the amount being transferred and the delivery method. Transfer fee and currency exchange rate may be subject to change by MoneyGram without notice.
        </Text>
      </View>
      <GradientBackground style={styles.card}>
        <View><View style={styles.viewTap}/></View>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("REVIEW_EXCHANGE_RATE")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>1 {data.sendCurrency} = {data.exchangeRate} {data.receiveCurrency}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={styles.feeLabel}>{t("REVIEW_FEES")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{data.fees} {data.sendCurrency}</Text>
          </View>
        </View>
        <View style={styles.Crow}>
          <Text style={[styles.feeLabel, { textDecorationLine: "underline"}]}>{t("REVIEW_TOTAL")}</Text>
          <View style={{ flexDirection: 'row'}}>
            <Text style={styles.oldPrice}>{data?.total} {data?.sendCurrency}</Text>
          </View>
        </View>
        <TouchableOpacity
          onPress={onContinue}
          activeOpacity={0.8}
          style={styles.continueButton}
        >
          <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
        </TouchableOpacity>
      </GradientBackground>
    </ScrollView>
    </View>
  );
};

const RowItem = ({
  label,
  value,
  editable,
  onEditStep,
}: {
  label: string;
  value: string;
  editable?: boolean;
  onEditStep?: () => void;
}) => (
  <View style={styles.row}>
    <View>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={styles.rowValue}>{value}</Text>
    </View>
    {editable && (
      <TouchableOpacity onPress={onEditStep}>
        <Text style={styles.editBtn}>Edit</Text>
      </TouchableOpacity>
    )}
  </View>
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Rubik',
    color: '#737373',
    lineHeight: 16,
    alignSelf: 'stretch'
    // marginBottom: 8,
  },
  amountInput: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pickUpText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: '#000',
    lineHeight: 20
  },
  amountText: {
    fontFamily: 'Rubik',
    fontSize: 32,
    fontWeight: '500',
    color: '#1f1f1f',
    lineHeight: 38
  },
  currencyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '500',
    color: '#8C8C8C',
    lineHeight: 18,
    paddingLeft: 6
  },
  editButton: {
    backgroundColor: '#E6E6E6',
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 16
  },
  editText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 18
  },
  CountryText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 24,
    paddingLeft: 6
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 48
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    alignItems: "center",
  },
  rowLabel: { fontSize: 14, color: "#555" },
  rowValue: { fontSize: 16, fontWeight: "600", marginTop: 2 },
  editBtn: { color: "#000", fontWeight: "500" },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.termsAndConditionsBlue,
    fontWeight: "500",
  },
  bold: { fontWeight: "600" },
  bottomCard: {
    borderTopWidth: 1,
    borderColor: "#eee",
    padding: 16,
    backgroundColor: "#fafafa",
  },
  total: { marginTop: 8, fontSize: 16, fontWeight: "700" },
  continueBtn: {
    marginTop: 16,
    backgroundColor: "#000",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  card: {
    alignItems: "center",
    justifyContent: 'center',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0
    // width: 327,
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius:24,
    marginBottom: 24,
    alignSelf: 'center'
  },
  promoContainer: {
    flexDirection: 'row',
    height: 56,
    padding: 24,
    borderRadius: 8,
    backgroundColor: Colors.searchCompanyBackground,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 24
  },
  promoInputWrapper: {
    flex: 1,
    position: 'relative',
    marginRight: 12,
  },
  promoInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3',
    paddingVertical: 8,
    paddingRight: 40,
    paddingLeft: 12,
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    bottom: 8,
    width: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearIconCircle: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: '#B3B3B3',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  promoText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.titleTextColor,
    lineHeight: 18
  },
  promoApplyButton: {
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#B3B3B3'
  },
  applyText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: Colors.applyText,
    lineHeight: 18,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  Crow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 16,
  },
  feeLabel: {
    fontSize: 16,
    fontWeight: "500",
  },
  oldPrice: {
    fontSize: 16,
    // textDecorationLine: "line-through",
    marginRight: 8,
  },
  newPrice: {
    fontSize: 16,
    fontWeight: "bold",
    color: "green",
  },
  button: {
    backgroundColor: "black",
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 24,
    // marginHorizontal: 30,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  buttonText: {
    color: "white",
    fontWeight: "bold",
    fontSize: 16,
  },
  continueButton: {
    alignSelf: "center",
    backgroundColor: Colors.black,
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: "center",
    shadowColor: Colors.black,
  },
  continueButtonText: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "500",
    color: Colors.white,
    lineHeight: 24
  },
});

export default ReviewFinalStep;